import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { CommentsService } from '../../service/comments.service';
import { SettingsService } from '../../service/settings.service';
import { deepCopy, removeUndefined, isDate, isTime, contentTypeSchemaValidatorTextFormat } from 'na-lib';

@Component({
    selector: 'app-work-task-template-field-rule-dialog',
    templateUrl: './work-task-template-field-rule-dialog.component.html',
    styleUrl: './work-task-template-field-rule-dialog.component.css',
    standalone: false
})

export class WorkTaskTemplateFieldRuleDialogComponent {

  ruleForm!:FormGroup
  ruleData:any
  ruleStructure:any
  tasktemplateUserRights_update:boolean
  tasktemplateUserRights_delete:boolean
  fieldType:string
  formatOptions = Object.values(contentTypeSchemaValidatorTextFormat)

  constructor(
    public dialogRef: MatDialogRef<WorkTaskTemplateFieldRuleDialogComponent>,
    private fb: FormBuilder,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private commentsService: CommentsService,
    private translate : TranslateService,
    private settingService: SettingsService
  ){
    dialogRef.disableClose = true;

  }

  ngOnInit(): void {
    this.tasktemplateUserRights_update = this.settingService.modulesRolesRights$.value['workTaskTemplates']['U']
    this.tasktemplateUserRights_delete = this.settingService.modulesRolesRights$.value['workTaskTemplates']['D']

    this.ruleForm = this.fb.group({
      type:[],//dateRange -- numericRange -- textRegex -- textLength
      min:[],
      max:[],
      regex:[],
      decimal:[],
      minDaysAgo:[],
      values:[],
      format:[],
      minExclusive:[],
      maxExclusive:[],
      multipleOf:[],
      errorMessage:[]
    })

    if(this.data.fieldType)
    this.fieldType = this.data.fieldType
    
    this.ruleData = deepCopy(this.data.rule)
    this.ruleStructure = deepCopy(this.data.ruleStructure)

    const { type, regex, decimal, minDaysAgo, errorMessage, values, format, minExclusive, maxExclusive, multipleOf } = this.ruleData
    let { min, max } = this.ruleData
    if(type == "dateRange"){
      if(min){
        min = new Date(min).toJSON()
        const min_date = isDate(min)
        const min_time = isTime(min)
        const min_infoDate = min_date.split('.')
        if(min_infoDate[0] && min_infoDate[1] && min_infoDate[2]){
          let newdate = min_infoDate[2]+'-'+min_infoDate[1]+'-'+min_infoDate[0]+ 'T' + min_time
          min = newdate  
        }
      }

      if(max){
        max = new Date(max).toJSON()
        const max_date = isDate(max)
        const max_time = isTime(max)
        const max_infoDate = max_date.split('.')
        if(max_infoDate[0] && max_infoDate[1] && max_infoDate[2]){
          let newdate = max_infoDate[2] + '-' + max_infoDate[1] + '-' + max_infoDate[0] + 'T' + max_time
          max = newdate
        }
      }
    }

    this.ruleForm.patchValue({
      type:type ? type : undefined,//dateRange -- numericRange -- textRegex -- textLength
      min:min ?? undefined,
      max:max ?? undefined,
      regex:regex ? regex : undefined,
      decimal: decimal === false,
      minDaysAgo:minDaysAgo ? minDaysAgo : undefined,
      values: Array.isArray(values) ? values.join('\n') : '',
      format: format || undefined,
      minExclusive: !!minExclusive,
      maxExclusive: !!maxExclusive,
      multipleOf: multipleOf ?? undefined,
      errorMessage:errorMessage ? errorMessage : undefined
    })

  }

  get validationErrorKey(): string | null {
    if (!this.ruleForm) return null;
    const { min, max, regex, values, format, minDaysAgo, multipleOf, decimal, minExclusive, maxExclusive } = this.ruleForm.value;
    const present = (value: unknown) => value !== null && value !== undefined && String(value).trim() !== '';
    const type = this.ruleData?.type;
    if (type === 'textLength') {
      if (!present(min) && !present(max)) return 'contentTypes.schemaRuleEmpty';
      if ([min, max].some(value => present(value) && (!Number.isInteger(Number(value)) || Number(value) < 0))) return 'contentTypes.schemaRuleInvalidNumber';
      return present(min) && present(max) && Number(min) > Number(max) ? 'contentTypes.schemaRuleInvalidRange' : null;
    }
    if (type === 'textRegex') {
      if (!present(regex)) return 'contentTypes.schemaRuleEmpty';
      try { new RegExp(regex); } catch { return 'contentTypes.schemaRuleInvalidRegex'; }
      return null;
    }
    if (type === 'textEnum' || type === 'numericEnum') {
      const entries = String(values || '').split(/\r?\n/).map(value => value.trim()).filter(Boolean);
      if (!entries.length) return 'contentTypes.schemaRuleEmpty';
      return type === 'numericEnum' && entries.some(value => !Number.isFinite(Number(value))) ? 'contentTypes.schemaRuleInvalidNumber' : null;
    }
    if (type === 'textFormat') return this.formatOptions.includes(format) ? null : 'contentTypes.schemaRuleEmpty';
    if (type === 'numericRange') {
      if ([min, max, multipleOf].some(value => present(value) && !Number.isFinite(Number(value)))) return 'contentTypes.schemaRuleInvalidNumber';
      if (present(multipleOf) && Number(multipleOf) <= 0) return 'contentTypes.schemaRuleInvalidNumber';
      if (present(min) && present(max) && (Number(min) > Number(max) || (Number(min) === Number(max) && (minExclusive || maxExclusive)))) return 'contentTypes.schemaRuleInvalidRange';
      return present(min) || present(max) || present(multipleOf) || decimal || this.fieldType === 'text' || this.fieldType === 'longText' ? null : 'contentTypes.schemaRuleEmpty';
    }
    if (type === 'dateRange') return present(min) || present(max) || present(minDaysAgo) ? null : 'contentTypes.schemaRuleEmpty';
    return null;
  }

  getRuleDescription(): string {
    return this.translate.instant(`contentTypes.schemaRuleDescriptions.${this.ruleData?.type || 'default'}`);
  }

  getRuleLabel(): string {
    const key = `contentTypes.schemaRuleLabels.${this.ruleData?.type}`;
    const label = this.translate.instant(key);
    return label === key ? this.translate.instant(this.ruleStructure?.translation) : label;
  }

  getParameterLabel(rule: any): string {
    if (rule.key === 'min' || rule.key === 'max') {
      const labels: Record<string, string> = {
        textLength: rule.key === 'min' ? 'minLength' : 'maxLength',
        numericRange: rule.key === 'min' ? 'minValue' : 'maxValue'
      };
      const label = labels[this.ruleData?.type];
      if (label) return this.translate.instant(`contentTypes.schemaRuleLimits.${label}`);
    }
    return this.translate.instant(rule.description);
  }

  getParameterHint(rule: any): string {
    const type = this.ruleData?.type;
    if (rule.key === 'values') return this.translate.instant('contentTypes.schemaAllowedValuesHint');
    if (rule.key === 'regex') return this.translate.instant('contentTypes.schemaRegexHint');
    if (rule.key === 'multipleOf') return this.translate.instant('contentTypes.schemaMultipleOfHint');
    if (rule.key === 'minDaysAgo') return this.translate.instant('contentTypes.schemaMinDaysAgoHint');
    if ((rule.key === 'min' || rule.key === 'max') && (type === 'textLength' || type === 'numericRange')) {
      const key = type === 'textLength' ? (rule.key === 'min' ? 'minLengthHint' : 'maxLengthHint') : (rule.key === 'min' ? 'minValueHint' : 'maxValueHint');
      return this.translate.instant(`contentTypes.schemaRuleLimits.${key}`);
    }
    return '';
  }

  getParameterPlaceholder(rule: any): string {
    if (rule.key === 'values' && this.ruleData?.type === 'textEnum') return this.translate.instant('contentTypes.schemaTextValuesExample');
    if (rule.key === 'regex') return '^[A-Z]{3}-[0-9]{3}$';
    if ((rule.key === 'min' || rule.key === 'max') && (this.ruleData?.type === 'textLength' || this.ruleData?.type === 'numericRange')) {
      const type = this.ruleData.type;
      const key = type === 'textLength' ? (rule.key === 'min' ? 'minLengthExample' : 'maxLengthExample') : (rule.key === 'min' ? 'minValueExample' : 'maxValueExample');
      return this.translate.instant(`contentTypes.schemaRuleLimits.${key}`);
    }
    return '';
  }

  saveRule(){
    if (this.validationErrorKey) return;
    if(this.data.rule && this.data.rule.type){
      let data
      const present = (value: unknown) => value !== null && value !== undefined && String(value).trim() !== ''
      const invalid = (key: string) => this.commentsService.addSnackBar.emit(this.translate.instant(key))
      switch(this.data.rule.type){
        case 'textLength':{
          const { type, min, max, errorMessage }= this.ruleForm.value
          if (!present(min) && !present(max)) return invalid('contentTypes.schemaRuleEmpty')
          if ((present(min) && (!Number.isInteger(Number(min)) || Number(min) < 0)) || (present(max) && (!Number.isInteger(Number(max)) || Number(max) < 0)) || (present(min) && present(max) && Number(min) > Number(max))) return invalid('contentTypes.schemaRuleInvalidRange')
          data = {type, min: present(min) ? Number(min): undefined, max: present(max) ? Number(max): undefined, errorMessage: errorMessage || undefined}
          break;
        }

        case 'textRegex':{
          const { type, regex, errorMessage } = this.ruleForm.value
          if (!present(regex)) return invalid('contentTypes.schemaRuleEmpty')
          try { new RegExp(regex) } catch { return invalid('contentTypes.schemaRuleInvalidRegex') }
          data = { type, regex, errorMessage: errorMessage || undefined }
          break;
        }

        case 'textEnum':
        case 'numericEnum': {
          const { type, values, errorMessage } = this.ruleForm.value
          const entries = String(values || '').split(/\r?\n/).map(value => value.trim()).filter(Boolean)
          if (!entries.length) return invalid('contentTypes.schemaRuleEmpty')
          if (type === 'numericEnum' && entries.some(value => !Number.isFinite(Number(value)))) return invalid('contentTypes.schemaRuleInvalidNumber')
          data = { type, values: type === 'numericEnum' ? entries.map(Number) : entries, errorMessage: errorMessage || undefined }
          break;
        }

        case 'textFormat': {
          const { type, format, errorMessage } = this.ruleForm.value
          if (!this.formatOptions.includes(format)) return invalid('contentTypes.schemaRuleEmpty')
          data = { type, format, errorMessage: errorMessage || undefined }
          break;
        }

        case 'numericRange':{
          const { type, min, max, decimal, minExclusive, maxExclusive, multipleOf, errorMessage } = this.ruleForm.value
          if ([min, max, multipleOf].some(value => present(value) && !Number.isFinite(Number(value)))) return invalid('contentTypes.schemaRuleInvalidNumber')
          if (present(multipleOf) && Number(multipleOf) <= 0) return invalid('contentTypes.schemaRuleInvalidNumber')
          if (present(min) && present(max) && (Number(min) > Number(max) || (Number(min) === Number(max) && (minExclusive || maxExclusive)))) return invalid('contentTypes.schemaRuleInvalidRange')
          if (!present(min) && !present(max) && !present(multipleOf) && !decimal && this.fieldType !== 'text' && this.fieldType !== 'longText') return invalid('contentTypes.schemaRuleEmpty')
          data = { type, min: present(min) ? Number(min) : undefined, max: present(max) ? Number(max) : undefined,
            decimal: decimal ? false : this.ruleData.decimal === true ? true : undefined, minExclusive: present(min) && minExclusive ? true : undefined,
            maxExclusive: present(max) && maxExclusive ? true : undefined,
            multipleOf: present(multipleOf) ? Number(multipleOf) : undefined, errorMessage: errorMessage || undefined }
          break;
        }

        case 'dateRange':{
          const { type, min, max, minDaysAgo, errorMessage } = this.ruleForm.value
          if (!present(min) && !present(max) && !present(minDaysAgo)) return invalid('contentTypes.schemaRuleEmpty')
          data = { type, min: min ? new Date(min): undefined, max: max ? new Date(max): undefined, minDaysAgo: minDaysAgo ? Number(minDaysAgo) : undefined, errorMessage: errorMessage ? errorMessage : undefined}
          break;
        }
      }
      if (data) {
        removeUndefined(data)
        this.dialogRef.close([true, data])
      }else{
        const message = this.translate.instant("SnackBarConstants.AT_LEAST_ONE")
        this.commentsService.addSnackBar.emit(message)
      }
    }
  }
}
